import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { CloudArrowUpIcon, ArrowDownTrayIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

interface FileUploadStepProps {
    onFileSelect: (file: File) => void;
    acceptedFormats: string[];
    sourceType: string;
    maxSizeBytes?: number;
}

const FileUploadStep: React.FC<FileUploadStepProps> = ({ 
    onFileSelect, 
    acceptedFormats, 
    sourceType,
    maxSizeBytes = 1024 * 1024 // Default to 1MB
}) => {
    const [fileError, setFileError] = useState<string | null>(null);
    
    const validateFile = (file: File): boolean => {
        // Check file size (default 1MB)
        if (file.size > maxSizeBytes) {
            setFileError(`File is too large. Maximum size is ${Math.round(maxSizeBytes/1024)}KB.`);
            return false;
        }
        
        // Check file type based on extension and mimetype
        const extension = file.name.split('.').pop()?.toLowerCase();
        const acceptedExtensions = ['csv', 'json', '1pif'];
        
        if (!acceptedExtensions.includes(extension || '')) {
            setFileError(`Invalid file type. Accepted formats: ${acceptedFormats.join(', ')}`);
            return false;
        }
        
        setFileError(null);
        return true;
    };

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            
            if (validateFile(file)) {
                onFileSelect(file);
            }
        }
    }, [onFileSelect, maxSizeBytes]);

    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        onDrop,
        accept: {
            'text/csv': ['.csv'],
            'application/json': ['.json'],
            'application/x-1password-1pif': ['.1pif'],
            'application/csv': ['.csv']
        },
        multiple: false,
        maxSize: maxSizeBytes
    });

    const handleDownloadTemplate = () => {
        const link = document.createElement('a');
        link.href = '/assets/templates/Atompass_sample_import_format.csv';
        link.download = 'Atompass_sample_import_format.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div>
            <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-6 py-4">
                <h3 className="text-lg font-semibold text-white flex items-center">
                    <CloudArrowUpIcon className="h-6 w-6 mr-3" />
                    📤 Upload Your File
                </h3>
                <p className="text-primary-100 text-sm mt-1">Upload your {sourceType} export file</p>
            </div>
            
            <div className="p-6">
                {sourceType === 'other' && (
                    <div className="mb-6 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-2xl p-6 border-2 border-blue-200 dark:border-blue-700">
                        <div className="flex items-start space-x-4">
                            <div className="p-3 bg-blue-500/20 rounded-xl">
                                <ArrowDownTrayIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex-1">
                                <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
                                    📋 Download Sample Format
                                </h4>
                                <p className="text-blue-800 dark:text-blue-200 mb-4 leading-relaxed">
                                    Before uploading your CSV file, download our sample format to ensure compatibility:
                                </p>
                                <ul className="list-disc pl-5 mb-4 space-y-2 text-sm text-blue-700 dark:text-blue-300">
                                    <li>Download our sample CSV format for reference</li>
                                    <li>Open the downloaded file in Google Sheets</li>
                                    <li>Do not modify column names to ensure successful import</li>
                                    <li>Use only the specified types: password, note, personal_details, card_details, bank_details</li>
                                    <li>Fill in the relevant fields based on the type of entry</li>
                                    <li>Download the updated file in .csv format</li>
                                    <li>Upload the newly downloaded CSV file for import</li>
                                </ul>
                                <button
                                    onClick={handleDownloadTemplate}
                                    className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                                >
                                    <ArrowDownTrayIcon className="h-5 w-5" />
                                    <span>📥 Download Template</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* File Requirements Card */}
                <div className="mb-6 bg-gradient-to-br from-yellow-50 to-amber-100 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-2xl p-6 border-2 border-yellow-200 dark:border-yellow-700">
                    <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-3 flex items-center">
                        <span className="text-2xl mr-2">⚠️</span>
                        File Requirements
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white/60 dark:bg-gray-700/60 rounded-lg p-4">
                            <p className="font-medium text-yellow-800 dark:text-yellow-200">Maximum file size:</p>
                            <p className="text-yellow-700 dark:text-yellow-300">1MB</p>
                        </div>
                        <div className="bg-white/60 dark:bg-gray-700/60 rounded-lg p-4">
                            <p className="font-medium text-yellow-800 dark:text-yellow-200">Supported formats:</p>
                            <p className="text-yellow-700 dark:text-yellow-300">{acceptedFormats.join(', ')}</p>
                        </div>
                    </div>
                </div>

                {/* File Upload Area */}
                <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-300 ${
                        isDragActive && !isDragReject 
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 
                        isDragReject || fileError 
                            ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 
                        'border-gray-300 dark:border-gray-600 hover:border-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                >
                    <input {...getInputProps()} />
                    {isDragReject || fileError ? (
                        <div className="space-y-4">
                            <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-full w-20 h-20 mx-auto flex items-center justify-center">
                                <ExclamationCircleIcon className="h-10 w-10 text-red-500" />
                            </div>
                            <div>
                                <p className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">
                                    ❌ Upload Error
                                </p>
                                <p className="text-red-600 dark:text-red-300">
                                    {fileError || "Invalid file type or size"}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="p-4 bg-primary-100 dark:bg-primary-900/30 rounded-full w-20 h-20 mx-auto flex items-center justify-center">
                                <CloudArrowUpIcon className="h-10 w-10 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div>
                                <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {isDragActive ? '📂 Drop the file here' : '📤 Upload Your File'}
                                </p>
                                <p className="text-gray-600 dark:text-gray-400">
                                    {isDragActive
                                        ? 'Release to upload your file'
                                        : 'Drag and drop your file here, or click to browse'}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Error Display */}
                {fileError && (
                    <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-700 rounded-xl">
                        <div className="flex items-center space-x-3">
                            <ExclamationCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0" />
                            <p className="text-red-700 dark:text-red-300 font-medium">
                                {fileError}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FileUploadStep; 